// GH #40 — launching with no network connection used to let whichever
// data-fetching component happened to run first throw an uncaught
// TypeError (a failed fetch leaving some field undefined/null), which
// ErrorBoundary then showed as a generic "Something broke" crash screen
// instead of a graceful, expected "you're offline" state.
//
// Rather than chasing every individual fetch call site that might not
// guard against a failed request (the same category of risk as the
// already-tracked ~275 unguarded-nullable-field TypeScript errors, GH
// #76), this detects the offline case up front — before any child
// component gets a chance to fetch anything at all.

import { useEffect, useState } from "react";

function isOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export function OfflineGate({ children }: { children: React.ReactNode }) {
  const [online, setOnline] = useState(isOnline);

  useEffect(() => {
    const goOnline  = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (!online) return <OfflineScreen onRetry={() => setOnline(isOnline())} />;
  return <>{children}</>;
}

function OfflineScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-lc-navy to-lc-blue-dark flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-xl p-8 shadow-2xl text-center">
        <div className="inline-flex w-12 h-12 rounded-xl bg-lc-blue-light text-lc-blue items-center justify-center mb-4">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
        </div>
        <h1 className="text-xl font-extrabold text-lc-ink mb-1.5">No internet connection</h1>
        <p className="text-sm text-lc-mute leading-relaxed mb-6">
          Check your connection and try again. Your data is safe — nothing was lost.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="w-full h-11 rounded-lc-md bg-gradient-to-br from-lc-blue to-lc-blue-dark text-white text-sm font-extrabold"
        >
          Try again
        </button>
      </div>
    </div>
  );
}
